import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Back from "../../../assets/aftersalesservice_icons/back.svg";
import Success from "../../../assets/aftersalesservice_icons/success_icon.png";
import Phone from "../../../assets/aftersalesservice_icons/phone.svg";
import Timer from "../../../assets/aftersalesservice_icons/timer.svg";
import Qr from "../../../assets/aftersalesservice_icons/qr.svg";
import FallbackImg from "../../../components/FallbackImg";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import ApiService from "../../../components/Api_services";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
} from "../serialTypeUtils";

const TicketSubmitted = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);
  const rawType = searchParams.get("type") || "";
  const type =
    rawType === "General_Maintenance"
      ? "General Maintenance"
      : rawType === "Repair_Service"
        ? "Repair Service"
        : rawType === "Technical_Consultation_Inquiry"
          ? "Technical Consultation / Inquiry"
          : rawType;

  const [ticketNo, setTicketNo] = useState("");
  const [ticketDate, setTicketDate] = useState("");
  const [countdown, setCountdown] = useState("24:00:00");

  useEffect(() => {
    const fetchTicketStatus = async () => {
      const serialNo = serialNoForApi;

      const payload = {
        serial_no: serialNo,
        serial_type: serialType,
        type: type,
      };

      try {
        const response = await ApiService.checkTicketStatus(payload);
        // console.log("ticket status:", response);

        if (response && response.status === 200) {
          setTicketNo(response.data.ticket_no);
          setTicketDate(response.data.ticket_date);
        }
      } catch (error) {
        console.error("Error fetching ticket status:", error);
      }
    };

    fetchTicketStatus();
  }, [serialNoForApi, serialType, type]);

  useEffect(() => {
    if (!ticketDate) return;
    const parsedDate = new Date(ticketDate.replace(/-/g, "/"));
    const expiryDate = new Date(parsedDate.getTime() + 24 * 60 * 60 * 1000);

    const pad = (n) => (n < 10 ? "0" + n : n);

    let interval;

    const updateCountdown = () => {
      const now = new Date();
      const diff = expiryDate - now;

      if (diff <= 0) {
        setCountdown("00:00:00");
        clearInterval(interval);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
    };

    updateCountdown();
    interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [ticketDate]);

  const goBack = (e) => {
    navigate(
      `/aftersalesservice/menucenter${buildAfterSalesQuery(searchParams)}`,
    );
  };

  return (
    <AfterSalesLayout>
      <div
        className="as-ticket-submission-content"
        id="ticket_submission_modal"
      >
        <div className="as-ticket-submitted-content">
          <div className="as-page">
            <div style={{ padding: "0 31.97px" }}>
              {/* Back Button */}
              <div className="as-header-card" style={{ textAlign: "left" }}>
                <button
                  className="as-back-btn"
                  id="ticket_submitted_back"
                  style={{ marginBottom: 0 }}
                  aria-label="Go back"
                  onClick={goBack}
                >
                  <FallbackImg src={Back} alt="back" width="20" height="20" />
                  <span>{trans.back}</span>
                </button>
              </div>

              {/* Success Green Box */}
              <div className="as-success-box">
                <div className="as-circle">
                  <div className="as-circle-inner">
                    <FallbackImg
                      src={Success}
                      alt="success"
                      style={{ marginTop: 35, width: 172, height: 186 }}
                    />
                  </div>
                </div>
                <h2 className="as-font-1">{trans.ticket_submitted}</h2>
                <p className="as-font-1">{trans.ticket_submitted_sub}</p>

                <div className="as-ticket-number">
                  <span className="as-font-1">{trans.ticket_no}</span>
                  <div className="as-ticket-number-div">
                    <span style={{ fontSize: 32, paddingTop: 30 }}>
                      {ticketNo}
                    </span>
                  </div>
                </div>
              </div>

              {/* What Happens Next */}
              <div className="as-card">
                <h3 className="as-font-1" style={{ margin: "10px 0px" }}>
                  {trans.what_happens}
                </h3>

                <div className="as-blue-call-box">
                  <div className="as-icon-text">
                    <div className="as-square">
                      <FallbackImg
                        src={Phone}
                        alt="phone"
                        width="35"
                        height="35"
                      />
                    </div>
                    <div className="as-text-block">
                      <strong className="as-font-2">{trans.we_contact}</strong>
                      <span className="as-font-3">(+60) 13-9898 993</span>
                    </div>
                  </div>

                  <div className="as-time-box">
                    <div className="as-time-box-leftside">
                      <FallbackImg
                        src={Timer}
                        alt="timer"
                        width="15"
                        height="15"
                      />
                      <span>{trans.expect_response}</span>
                    </div>
                    <strong className="as-font-3">{countdown}</strong>
                  </div>
                </div>

                <div className="as-step-box">
                  {/* Step 1 */}
                  <div className="as-step-item">
                    <div className="as-step-left">
                      <div className="as-step-number as-blue">1</div>
                      <div className="as-step-line"></div>
                    </div>
                    <div className="as-steps-content">
                      <h4>{trans.phone_call}</h4>
                      <p>{trans.phone_call_sub}</p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="as-step-item">
                    <div className="as-step-left">
                      <div className="as-step-number as-green">2</div>
                    </div>
                    <div className="as-steps-content">
                      <h4>{trans.whatsapp}</h4>
                      <p>
                        {trans.whatsapp_sub_1}
                        <span className="as-wa">{trans.whatsapp_sub_2}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Tracking Box */}
              <div className="as-qr-card">
                <div className="as-icon-text">
                  <div className="as-square-1">
                    <FallbackImg src={Qr} alt="qr" width="35" height="35" />
                  </div>
                  <div className="as-text-block" style={{ textAlign: "left" }}>
                    <strong className="as-font-4" style={{ marginBottom: 10 }}>
                      {trans.track_req}
                    </strong>
                    <span className="as-font-1">{trans.track_req_sub}</span>
                  </div>
                </div>
              </div>

              <button
                className="as-btn-done as-font-1"
                id="ticket_submitted_done"
                onClick={goBack}
              >
                {trans.done}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default TicketSubmitted;
